/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { canadianPayrollAPI } from '../../lib/api';
import { Loader2, Leaf } from 'lucide-react';

/**
 * YTDSummaryCard — Year-to-Date totals for a single CA employee.
 *
 * Read-only snapshot. Backend aggregates from immutable payslips (60s cached).
 * Mounted only when tenant is CA-eligible AND the target employee is CA-eligible.
 * If the API returns 403 (non-CA employee) the card hides itself silently.
 */
export function YTDSummaryCard({ employeeId, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const load = useCallback(async () => {
    if (!employeeId) return;
    try {
      const r = await canadianPayrollAPI.ytd(employeeId, year);
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 403) setHidden(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, year]);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  return (
    <Card className="border-slate-200" data-testid="ytd-summary-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Leaf className="text-emerald-600" size={16} />
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Year-to-Date Summary
            </h3>
          </div>
          {data?.province_label && (
            <span className="text-xs text-slate-500" data-testid="ytd-province-label">
              {data.province_label}
            </span>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading YTD…
          </div>
        )}

        {!loading && data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ytd-summary-grid">
            <Cell label="Gross"        value={fmt(data.gross_ytd)}        accent="slate" />
            <Cell label="Deductions"   value={fmt(data.deductions_ytd)}   accent="rose" />
            <Cell label="Net"          value={fmt(data.net_ytd)}          accent="emerald" />
            <Cell label="Overtime"     value={fmt(data.overtime_ytd)}     accent="amber" />
            <Cell label="Holiday"      value={fmt(data.holiday_pay_ytd)}  accent="indigo" />
            <Cell label="Vacation"     value={fmt(data.vacation_pay_ytd)} accent="teal" />
            <Cell label="Bonus"        value={fmt(data.bonus_ytd)}        accent="violet" />
            <Cell label="Tips"         value={fmt(data.tips_ytd)}         accent="blue" />
          </div>
        )}

        {!loading && data && (
          <p className="text-xs text-slate-400 mt-3" data-testid="ytd-payslip-count">
            Aggregated from {data.payslip_count} payslip{data.payslip_count === 1 ? '' : 's'} • Year {data.year}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const ACCENTS = {
  slate:   'bg-slate-50 text-slate-700',
  rose:    'bg-rose-50 text-rose-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber:   'bg-amber-50 text-amber-800',
  indigo:  'bg-indigo-50 text-indigo-700',
  teal:    'bg-teal-50 text-teal-700',
  violet:  'bg-violet-50 text-violet-700',
  blue:    'bg-blue-50 text-blue-700',
};

function Cell({ label, value, accent }) {
  return (
    <div className={`rounded-md p-3 ${ACCENTS[accent] || ACCENTS.slate}`} data-testid={`ytd-cell-${label.toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-base font-bold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

export default YTDSummaryCard;
